import { theme } from "@pepsico/layout";
import type { Map, SymbolLayerSpecification } from "mapbox-gl";

import {
  GROWER_LABEL_BG_COLOR,
  LABEL_PLAQUE_PADDING,
  PLOT_LABEL_BG_COLOR,
  ROW_LABEL_BG_COLOR,
  SITE_LABEL_BG_COLOR,
  TRIAL_LABEL_BG_COLOR,
} from "./mapConstants";

export const LABEL_PLAQUE_IMAGE_IDS = {
  grower: "plot-label-plaque-grower",
  location: "plot-label-plaque-location",
  plot: "plot-label-plaque-plot",
  row: "plot-label-plaque-row",
  site: "plot-label-plaque-site",
  trial: "plot-label-plaque-trial",
} as const;

const PLAQUE_PIXEL_RATIO = 2;
const PLAQUE_SIZE = 32;
const STRETCH_RADIUS = 8;
const PLAQUE_STRETCH: [number, number] = [8, 24];
const PLAQUE_CONTENT: [number, number, number, number] = [6, 6, 26, 26];
/**
 * Rigid plaques scale as one image. This bitmap is 32× the on-screen layout
 * size so a long grower, plot, or site name shrinks the image instead of
 * enlarging a 32px texture. The inset stays inside the corner radius.
 */
const RIGID_PLAQUE_SCALE = 32;
const RIGID_PLAQUE_SIZE = PLAQUE_SIZE * RIGID_PLAQUE_SCALE;
const RIGID_PLAQUE_CONTENT: [number, number, number, number] = [
  2 * RIGID_PLAQUE_SCALE,
  2 * RIGID_PLAQUE_SCALE,
  30 * RIGID_PLAQUE_SCALE,
  30 * RIGID_PLAQUE_SCALE,
];
const RIGID_RADIUS = 4 * RIGID_PLAQUE_SCALE;

const LABEL_BG_COLORS: Record<string, string> = {
  [LABEL_PLAQUE_IMAGE_IDS.grower]: GROWER_LABEL_BG_COLOR,
  [LABEL_PLAQUE_IMAGE_IDS.location]: theme.palette.info.main,
  [LABEL_PLAQUE_IMAGE_IDS.plot]: PLOT_LABEL_BG_COLOR,
  [LABEL_PLAQUE_IMAGE_IDS.row]: ROW_LABEL_BG_COLOR,
  [LABEL_PLAQUE_IMAGE_IDS.site]: SITE_LABEL_BG_COLOR,
  [LABEL_PLAQUE_IMAGE_IDS.trial]: TRIAL_LABEL_BG_COLOR,
};

/**
 * Stretch plaques keep round corners as the label grows. Rotating that slice
 * grid warps it into a star, so plaques that follow a line or a plot's long
 * axis scale as one image.
 */
const RIGID_PLAQUE_IDS = new Set<string>([
  LABEL_PLAQUE_IMAGE_IDS.grower,
  LABEL_PLAQUE_IMAGE_IDS.plot,
  LABEL_PLAQUE_IMAGE_IDS.row,
  LABEL_PLAQUE_IMAGE_IDS.site,
]);

type SymbolLayout = NonNullable<SymbolLayerSpecification["layout"]>;

interface LabelPlaqueLayoutOptions {
  iconAnchor?: "bottom" | "top";
  iconImage: string;
  iconRotationAlignment?: "map" | "viewport";
}

const fillRoundedRect = (
  context: CanvasRenderingContext2D,
  color: string,
  radius: number,
  size: number,
): void => {
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(radius, 0);
  context.arcTo(size, 0, size, size, radius);
  context.arcTo(size, size, 0, size, radius);
  context.arcTo(0, size, 0, 0, radius);
  context.arcTo(0, 0, size, 0, radius);
  context.closePath();
  context.fill();
};

const createLabelPlaqueImage = (
  color: string,
  radius: number,
  size: number,
): ImageData | null => {
  const canvas = document.createElement("canvas");
  canvas.height = size;
  canvas.width = size;
  const context = canvas.getContext("2d");
  if (context == null) {
    return null;
  }
  fillRoundedRect(context, color, radius, size);
  return context.getImageData(0, 0, size, size);
};

const addLabelPlaqueImage = (map: Map, imageId: string): void => {
  if (map.hasImage(imageId)) {
    return;
  }
  const color = LABEL_BG_COLORS[imageId];
  if (color == null) {
    return;
  }
  const isRigid = RIGID_PLAQUE_IDS.has(imageId);
  const image = createLabelPlaqueImage(
    color,
    isRigid ? RIGID_RADIUS : STRETCH_RADIUS,
    isRigid ? RIGID_PLAQUE_SIZE : PLAQUE_SIZE,
  );
  if (image == null) {
    return;
  }
  map.addImage(imageId, image, {
    content: isRigid ? RIGID_PLAQUE_CONTENT : PLAQUE_CONTENT,
    pixelRatio: PLAQUE_PIXEL_RATIO,
    ...(isRigid
      ? {}
      : { stretchX: [PLAQUE_STRETCH], stretchY: [PLAQUE_STRETCH] }),
  });
};

export const addLabelPlaqueImages = (map: Map): void => {
  Object.keys(LABEL_BG_COLORS).forEach((imageId) => {
    addLabelPlaqueImage(map, imageId);
  });
};

export const labelPlaqueLayout = ({
  iconAnchor,
  iconImage,
  iconRotationAlignment,
}: LabelPlaqueLayoutOptions): SymbolLayout => ({
  "icon-allow-overlap": true,
  ...(iconAnchor == null ? {} : { "icon-anchor": iconAnchor }),
  "icon-ignore-placement": true,
  "icon-image": iconImage,
  ...(iconRotationAlignment == null
    ? {}
    : { "icon-rotation-alignment": iconRotationAlignment }),
  "icon-text-fit": "both",
  "icon-text-fit-padding": [
    LABEL_PLAQUE_PADDING,
    LABEL_PLAQUE_PADDING,
    LABEL_PLAQUE_PADDING,
    LABEL_PLAQUE_PADDING,
  ],
});
